import {
  IconAlertCircle,
  IconAward,
  IconBulb,
  IconBulbOff,
  IconFlag,
  IconMessage,
  IconX,
} from '@rossum/ui/icons/tabler';
import {
  Chip,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useIntlDate } from '../../i18n/useIntlDate';
import {
  isFeatureGenerallyAvailable,
  isFeatureLocallyOverridden,
} from './featureEnablement';
import { FeatureFeedbackCollector } from './FeatureFeedbackCollector';
import { Feature, FeatureId, isLabFeature } from './features';

type FeatureConfig = {
  enabled: boolean;
  askForFeedback?: boolean;
};

type FeatureTogglesProps = {
  features: Feature[];
  config: Partial<Record<FeatureId, FeatureConfig>>;
  readonly?: boolean;
  onChange?: (featureId: FeatureId, value: boolean | null) => void;
  clearable?: boolean;
  collectFeedback?: boolean;
  onFeedbackSubmit?: (featureId: FeatureId, feedback: string) => void;
  onFeedbackReject?: (featureId: FeatureId) => void;
};

export const FeatureToggles = ({
  features,
  config,
  readonly = false,
  onChange,
  clearable = false,
  collectFeedback = false,
  onFeedbackSubmit,
  onFeedbackReject,
}: FeatureTogglesProps) => {
  const intl = useIntl();

  const { formatDate } = useIntlDate();

  const [showFeedbackForFeature, setShowFeedbackForFeature] =
    useState<FeatureId | null>(null);

  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);

  const onFeatureFeedbackSubmit = useCallback(
    (featureId: FeatureId, feedback: string) => {
      onFeedbackSubmit?.(featureId, feedback);
      setShowFeedbackPrompt(false);
      setShowFeedbackForFeature(null);
    },
    [setShowFeedbackPrompt, setShowFeedbackForFeature, onFeedbackSubmit]
  );

  const onFeatureFeedbackReject = useCallback(
    (featureId: FeatureId) => {
      onFeedbackReject?.(featureId);
      setShowFeedbackPrompt(false);
      setShowFeedbackForFeature(null);
    },
    [setShowFeedbackPrompt, setShowFeedbackForFeature, onFeedbackReject]
  );

  const handleToggle = useCallback(
    (flagName: FeatureId, enabled: boolean) => {
      if (!readonly && onChange) {
        onChange(flagName, enabled);
        if (collectFeedback && !enabled && !!config[flagName]?.askForFeedback) {
          setShowFeedbackForFeature(flagName);
          setShowFeedbackPrompt(true);
        }
      }
    },
    [readonly, onChange, collectFeedback, config]
  );

  return (
    <Stack sx={{ height: '100%', width: '100%' }} spacing={2}>
      <List disablePadding dense sx={{ width: '100%' }}>
        {features.map(feature => {
          const enabled = !!config[feature.id]?.enabled;

          return (
            <React.Fragment key={feature.id}>
              <ListItem
                sx={{
                  px: 0,
                  borderLeft: '2px solid',
                  borderLeftColor: theme =>
                    !readonly && isFeatureLocallyOverridden(feature.id)
                      ? theme.palette.warning.main
                      : 'transparent',
                  // TODO: This doesn't work with listItemClasses for some reason, likely MUI bug
                  [`& .MuiListItemSecondaryAction-root`]: {
                    alignSelf: 'flex-start',
                    position: 'relative',
                    transform: 'none',
                    top: 0,
                  },
                }}
                secondaryAction={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                      top: 0,
                    }}
                  >
                    {collectFeedback && (
                      <Tooltip
                        title={intl.formatMessage({
                          id: 'components.featureToggles.giveFeedback',
                        })}
                      >
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() =>
                            setShowFeedbackForFeature(currentFeatureId =>
                              currentFeatureId === feature.id
                                ? null
                                : feature.id
                            )
                          }
                          data-cy={`feature-toggle-feedback-button-${feature.id}`}
                        >
                          <SvgIcon fontSize="small">
                            <IconMessage />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    )}
                    {isLabFeature(feature) ? (
                      <Tooltip
                        title={
                          isFeatureGenerallyAvailable(feature.id)
                            ? intl.formatMessage({
                                id: 'components.featureToggles.featureGA',
                              })
                            : intl.formatMessage(
                                {
                                  id: 'components.featureToggles.featureNotGA',
                                },
                                {
                                  date: formatDate(feature.gaDate),
                                }
                              )
                        }
                      >
                        <Chip
                          icon={
                            <SvgIcon fontSize="small">
                              {isFeatureGenerallyAvailable(feature.id) ? (
                                <IconAward />
                              ) : (
                                <IconFlag />
                              )}
                            </SvgIcon>
                          }
                          label={formatDate(feature.gaDate)}
                          size="small"
                          color={
                            isFeatureGenerallyAvailable(feature.id)
                              ? 'success'
                              : 'info'
                          }
                          sx={{
                            cursor: 'default',
                          }}
                          variant="filled"
                        />
                      </Tooltip>
                    ) : null}
                    {!readonly && isFeatureLocallyOverridden(feature.id) ? (
                      <Tooltip
                        title={intl.formatMessage({
                          id: 'components.featureToggles.featureHasLocalOverrides',
                        })}
                      >
                        <SvgIcon color="warning" fontSize="small">
                          <IconAlertCircle />
                        </SvgIcon>
                      </Tooltip>
                    ) : null}
                    {!readonly && clearable ? (
                      <IconButton
                        edge="end"
                        onClick={() =>
                          !readonly && onChange?.(feature.id, null)
                        }
                        disabled={readonly}
                        size="small"
                        data-cy={`feature-toggle-clear-${feature.id}`}
                      >
                        <SvgIcon color="action" fontSize="small">
                          <IconX />
                        </SvgIcon>
                      </IconButton>
                    ) : null}
                  </Stack>
                }
              >
                <ListItemIcon
                  sx={{ minWidth: 40, alignSelf: 'flex-start', pt: 1 }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton
                      edge="end"
                      aria-label={
                        enabled
                          ? intl.formatMessage({
                              id: 'components.featureToggles.disableFeature',
                            })
                          : intl.formatMessage({
                              id: 'components.featureToggles.enableFeature',
                            })
                      }
                      color={enabled ? 'success' : 'default'}
                      onClick={() => handleToggle(feature.id, !enabled)}
                      disabled={readonly}
                      data-cy={`feature-toggle-icon-${feature.id}`}
                    >
                      {enabled ? (
                        <SvgIcon color="success" fontSize="small">
                          <IconBulb />
                        </SvgIcon>
                      ) : (
                        <SvgIcon color="disabled" fontSize="small">
                          <IconBulbOff />
                        </SvgIcon>
                      )}
                    </IconButton>
                  </Stack>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {isLabFeature(feature) ? feature.name : feature.id}
                    </Typography>
                  }
                  secondary={feature.description || null}
                />
              </ListItem>
              <Grow
                in={
                  collectFeedback &&
                  showFeedbackForFeature === feature.id &&
                  !isFeatureLocallyOverridden(feature.id)
                }
                appear
                unmountOnExit
              >
                <div>
                  <FeatureFeedbackCollector
                    featureId={feature.id}
                    onFeedbackSubmit={onFeatureFeedbackSubmit}
                    onFeedbackReject={onFeatureFeedbackReject}
                    showPrompt={showFeedbackPrompt}
                  />
                </div>
              </Grow>
            </React.Fragment>
          );
        })}
      </List>
    </Stack>
  );
};
